/* eslint-disable @next/next/no-img-element */
import {
  basicPageProps,
  fetchTemplate,
  isServer,
  mobileProps,
} from '@/utils/isServer';
import Title from '@components2/Landing/Title';
import { absoluteServerPath, CDN1, CDN2, resolveConfigUrl } from '@helpers/api';
import SearchComponent from '@components/Search';
import { cacheFetch } from '@helpers/cacheFetch';
import { Collapse, Divider, Empty } from 'antd';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import NoData from '@/components2/NoData';
import dayjs from 'dayjs';
import SearchStore from '@/stores/SearchStore';
import { useMobxStores } from '@/stores';
import CardList from '@/components2/CardList';

const SectionItem = dynamic(() => import('@components2/Landing/SectionItem'), {
  ssr: true,
});

const Airports = dynamic(() => import('@components/Airports'), {
  ssr: true,
});

const Guides = dynamic(() => import('@components/Guides'), {
  ssr: true,
});

const Monthlies = dynamic(() => import('@components/Monthlies'), {
  ssr: true,
});

const { Panel } = Collapse;

function extractPrefixSuffix(url) {
  let supressFromHome = false;
  let suffix = '';
  let prefix = '/event/';
  if (url.indexOf('?') != -1) {
    suffix = url.substring(url.indexOf('?'));
  }
  if (url.indexOf('/myapp') != -1) {
    prefix = '/myapp/event/';
  }
  if (suffix == '?fromhome=true') {
    suffix = '';
    supressFromHome = true;
  }
  return [prefix, suffix, supressFromHome];
}

// // This also gets called at build time
export const getServerSideProps: GetServerSideProps = async ({
  req,
  params,
  query,
}) => {
  const { isMobile } = mobileProps({ req });
  const { configInfo, airports, baseUrl } = await basicPageProps({ req });
  const { t } = query; // Preview
  let landing;
  const friendlyUrl = params.friendlyUrl as string;
  try {
    if (t === 'preview') {
      const landingResponse = await fetch(
        `${baseUrl}/api/AirportLandingEvent/${friendlyUrl}?t=preview`,
        { ...fetchTemplate }
      );
      landing = await landingResponse.json();
    } else {
      landing = await cacheFetch(
        `${baseUrl}/api/AirportLandingEvent/${friendlyUrl}`
      );
    }
    if (
      t !== 'preview' &&
      landing.monthly_friendly_url &&
      landing.monthly_friendly_url != friendlyUrl
    ) {
      const [prefix, suffix] = extractPrefixSuffix(req.url);
      return {
        redirect: {
          destination: prefix + landing.monthly_friendly_url + suffix,
          permanent: true,
        },
      } as any;
    }
  } catch (e) {
    return {
      redirect: {
        destination: '/',
      },
    } as any;
  }
  const [prefix, suffix, supressFromHome] = extractPrefixSuffix(req.url);
  if (supressFromHome) {
    return {
      redirect: {
        destination: prefix + landing.airport_frienldy_url + suffix,
        permanent: true,
      },
    } as any;
  }

  return {
    props: {
      airports,
      landing,
      configInfo,
      deviceType: isMobile ? 'mobile' : 'desktop',
    },
  };
};

// pages/parking/[friendlyUrl].js
function Event({ airports, landing, configInfo, deviceType }) {
  const landingAirportInitials = landing?.properties?.airport_initials;
  const pageTitle = landing?.properties?.seo_title
    ? landing?.properties?.seo_title
    : `${landing?.properties?.airport_name ?? ''} (${
        landingAirportInitials ?? ''
      }) | On Air Parking Cheap Discount Rates`;
  const metaTitle = pageTitle;
  const metaStaticBlurb = `Cheap ${
    landing?.properties?.airport_name ?? ''
  } Parking At ${
    landingAirportInitials ?? ''
  }. We know, the price looks to good to be true! Don't worry, it's true, and it's a great location. In fact you'll be parking at a professional parking facility. You'll be e-mailed where you're parking for $2.75 a day once you make the purchase on our site. Reserve today!`;
  const metaDescription = landing?.properties?.landing_seo_description
    ? landing?.properties?.landing_seo_description
    : metaStaticBlurb;
  const metaKeywords = landing?.landing_seo_keywords ?? '';
  const refLanding = useRef(null);
  const [showBar, setShowBar] = useState(true);
  const router = useRouter();

  const {
    searchStore,
  }: {
    searchStore: SearchStore;
  } = useMobxStores();

  useEffect(() => {
    const query = { ...router.query };
    query.facility_id = landing?.extra?.event_facility_id;
    query.checkin = landing?.extra?.event_checkin;
    query.checkout = landing?.extra?.event_checkout;
    searchStore.fetchSearchResult(null, query);
  }, [router, router?.query, searchStore]);

  return (
    <div className="relative">
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" content={metaTitle} key="og:title" />
        <meta
          property="og:description"
          content={metaDescription}
          key="og:description"
        />
        <meta name="description" content={metaDescription} key="description" />
        {metaKeywords && (
          <meta name="keywords" content={metaKeywords} key="keywords" />
        )}
        {landing?.metainfo?.map((meta) => meta.content)}
        {landing?.properties?.landing_display_header}
        {landing?.schemas?.map((schema, index) => (
          <script
            type="application/ld+json"
            key={index}
            dangerouslySetInnerHTML={{
              __html: schema.schema_object,
            }}
          ></script>
        ))}
      </Head>
      <div className={`sm:block sm:h-98 md:min-h-full md:h-72 relative`}>
        {landing?.extra.event_image ? (
          <img
            src={`${CDN1}/upload/${
              configInfo.isSandBoxEnvironment
                ? 1
                : configInfo.isProductionEnvironment
                ? 2
                : 0
            }/${landing?.extra.event_image}`}
            className="z-0 w-full object-cover min-h-64 h-40 md:h-full md:absolute object-bottom"
            alt="Event Background"
          />
        ) : (
          <img
            src={CDN2() + '/staticmyapp/gradient.png'}
            className="z-0 w-full object-cover min-h-64 h-40 md:h-full md:absolute object-bottom"
            alt="Event Background"
          />
        )}
      </div>
      <>
        <div>
          <div className="bg-[#FEF3EB] flex flex-col">
            <div className={`md:mx-auto md:overflow-hidden grid grid-cols-1`}>
              {searchStore.searchResult?.result?.length == 0 ? (
                <NoData
                  withoutBtn={true}
                  bgImageWhite={true}
                  textNoData="No parking now, but don't worry! Exciting event parking options will be launching soon!"
                  iconNoData
                />
              ) : (
                <>
                  <CardList
                    configInfo={configInfo}
                    searchAlternate={null}
                    checkin={landing?.extra?.event_checkin}
                    checkout={landing?.extra?.event_checkout}
                  />
                  {/* <div className="flex flex-row justify-center my-10 gap-x-2">
                    <div className="flex flex-col">
                      <Image
                        src={CDN2() + '/staticmyapp/dollar.svg'}
                        width={45}
                        height={45}
                        alt="dollar image"
                      />
                      <p className="text-[#515356] text-center mt-4 font-bold">
                        The lowest price guaranteed{' '}
                      </p>
                    </div>
                    <div className="flex flex-col">
                      <Image
                        src={CDN2() + '/staticmyapp/fcancellation.svg'}
                        width={45}
                        height={45}
                        alt="free cancellation image"
                      />
                      <p className="text-[#515356] text-center mt-4 font-bold">
                        Free cancellation
                      </p>
                    </div>
                    <div className="flex flex-col">
                      <Image
                        src={CDN2() + '/staticmyapp/fshuttle.svg'}
                        width={45}
                        height={45}
                        alt="free shuttle image"
                      />
                      <p className="text-[#515356] text-center mt-4 font-bold">
                        Free shuttle service
                      </p>
                    </div>
                  </div> */}
                </>
              )}
            </div>
          </div>
        </div>
      </>
      <div
        className="landing high-content container max-w-4xl mx-auto px-4 h-screen overflow-hidden relative"
        ref={refLanding}
      >
        <article>
          <h2 className="text-2xl text-center px-4">{landing?.page_title}</h2>
          <div className="high-content flex flex-col gap-2">
            {landing?.sections
              ?.sort((a, b) => {
                return a.order - b.order;
              })
              .map((section, index) =>
                section.type !== 'Accordion' ? (
                  <section key={index}>
                    <header>
                      {section?.tag && <Title value={section?.tag} />}
                    </header>
                    <SectionItem
                      section={section}
                      configInfo={configInfo}
                      fallbackImageAlt={landing?.page_title}
                    />
                  </section>
                ) : (
                  <Collapse collapsible="header" key={index}>
                    <Panel header={section?.tag} key={index}>
                      <SectionItem
                        section={section}
                        configInfo={configInfo}
                        fallbackImageAlt={landing?.page_title}
                      />
                    </Panel>
                  </Collapse>
                )
              )}
          </div>
        </article>
        {showBar && (
          <div
            id="content-bar"
            className="absolute w-full left-0 flex items-center justify-center bottom-0 h-12 cursor-pointer"
            onClick={() => {
              setShowBar(false);
              refLanding.current.classList.remove('h-screen');
              refLanding.current.classList.remove('overflow-hidden');
            }}
          >
            <div className="mx-auto flex itens-center justify-center font-medium text-lg">
              Click to read more
            </div>
          </div>
        )}
      </div>

      <Airports airports={airports} />
      <Guides airports={airports} />
      <Monthlies airports={airports} />
    </div>
  );
}

export default observer(Event);
